// Drawing App - View Transform (Zoom/Pan)

import { ViewTransform, Point } from './types';

export class TransformManager {
  private transform: ViewTransform = { offsetX: 0, offsetY: 0, scale: 1 };
  private minScale = 0.1;
  private maxScale = 20;
  private isPanning = false;
  private lastPanPoint = { x: 0, y: 0 };
  private onChangeCallback: (() => void) | null = null;

  // Pinch zoom state
  private initialPinchDistance = 0;
  private initialPinchScale = 1;

  getTransform(): ViewTransform {
    return { ...this.transform };
  }

  setTransform(t: ViewTransform): void {
    this.transform = { ...t };
    this.notifyChange();
  }

  onChange(cb: () => void): void {
    this.onChangeCallback = cb;
  }

  private notifyChange(): void {
    if (this.onChangeCallback) this.onChangeCallback();
  }

  screenToCanvas(screenX: number, screenY: number): { x: number; y: number } {
    return {
      x: (screenX - this.transform.offsetX) / this.transform.scale,
      y: (screenY - this.transform.offsetY) / this.transform.scale
    };
  }

  canvasToScreen(canvasX: number, canvasY: number): { x: number; y: number } {
    return {
      x: canvasX * this.transform.scale + this.transform.offsetX,
      y: canvasY * this.transform.scale + this.transform.offsetY
    };
  }

  zoom(delta: number, centerX: number, centerY: number): void {
    const factor = delta > 0 ? 0.9 : 1.1;
    const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.transform.scale * factor));

    // Zoom toward cursor position
    const scaleRatio = newScale / this.transform.scale;
    this.transform.offsetX = centerX - (centerX - this.transform.offsetX) * scaleRatio;
    this.transform.offsetY = centerY - (centerY - this.transform.offsetY) * scaleRatio;
    this.transform.scale = newScale;
    this.notifyChange();
  }

  zoomTo(scale: number, centerX: number, centerY: number): void {
    const newScale = Math.max(this.minScale, Math.min(this.maxScale, scale));
    const scaleRatio = newScale / this.transform.scale;
    this.transform.offsetX = centerX - (centerX - this.transform.offsetX) * scaleRatio;
    this.transform.offsetY = centerY - (centerY - this.transform.offsetY) * scaleRatio;
    this.transform.scale = newScale;
    this.notifyChange();
  }

  startPan(screenX: number, screenY: number): void {
    this.isPanning = true;
    this.lastPanPoint = { x: screenX, y: screenY };
  }

  movePan(screenX: number, screenY: number): void {
    if (!this.isPanning) return;
    this.transform.offsetX += screenX - this.lastPanPoint.x;
    this.transform.offsetY += screenY - this.lastPanPoint.y;
    this.lastPanPoint = { x: screenX, y: screenY };
    this.notifyChange();
  }

  endPan(): void {
    this.isPanning = false;
  }

  getIsPanning(): boolean {
    return this.isPanning;
  }

  startPinch(touch1: { x: number; y: number }, touch2: { x: number; y: number }): void {
    this.initialPinchDistance = Math.hypot(touch2.x - touch1.x, touch2.y - touch1.y);
    this.initialPinchScale = this.transform.scale;
  }

  movePinch(touch1: { x: number; y: number }, touch2: { x: number; y: number }): void {
    const distance = Math.hypot(touch2.x - touch1.x, touch2.y - touch1.y);
    const centerX = (touch1.x + touch2.x) / 2;
    const centerY = (touch1.y + touch2.y) / 2;
    const newScale = this.initialPinchScale * (distance / this.initialPinchDistance);
    this.zoomTo(newScale, centerX, centerY);
  }

  resetView(canvasWidth: number, canvasHeight: number): void {
    this.transform = { offsetX: 0, offsetY: 0, scale: 1 };
    this.notifyChange();
  }

  fitToScreen(contentWidth: number, contentHeight: number, viewWidth: number, viewHeight: number): void {
    const scaleX = viewWidth / contentWidth;
    const scaleY = viewHeight / contentHeight;
    const scale = Math.min(scaleX, scaleY, 1);
    this.transform.scale = scale;
    this.transform.offsetX = (viewWidth - contentWidth * scale) / 2;
    this.transform.offsetY = (viewHeight - contentHeight * scale) / 2;
    this.notifyChange();
  }

  getScalePercent(): number {
    return Math.round(this.transform.scale * 100);
  }
}
